/** The browser implementation, and the fallback every capability must have. */
import type { CapturedFile, Platform } from '@/platform/types'
import { webStorage } from '@/platform/web/storage'

function pickViaInput(
  accept: string[],
  multiple: boolean,
  capture: 'environment' | null,
): Promise<CapturedFile[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept.join(',')
    input.multiple = multiple
    // Opens the rear camera directly on a phone browser. Absent on desktop,
    // where it would be meaningless.
    if (capture) input.setAttribute('capture', capture)

    // In the document, not detached: a detached input happens to open the
    // picker in Chrome but is not guaranteed elsewhere.
    input.style.position = 'fixed'
    input.style.left = '-9999px'
    input.setAttribute('aria-hidden', 'true')
    input.tabIndex = -1
    document.body.appendChild(input)

    const cleanUp = () => input.remove()

    input.addEventListener('change', () => {
      const files = Array.from(input.files ?? [])
      resolve(
        files.map((file) => ({
          blob: file,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
        })),
      )
      cleanUp()
    })
    // Fires when the picker is dismissed without choosing anything. Without it
    // the element would linger in the DOM after every cancelled pick.
    input.addEventListener('cancel', () => {
      resolve([])
      cleanUp()
    })
    input.click()
  })
}

export const webPlatform: Platform = {
  kind: 'web',

  camera: {
    capture: ({ multiple }) => pickViaInput(['image/*'], multiple, 'environment'),
    pickFiles: ({ accept, multiple }) => pickViaInput(accept, multiple, null),
  },

  storage: webStorage,

  network: {
    isOnline: () => navigator.onLine,
    onChange(listener) {
      const online = () => listener(true)
      const offline = () => listener(false)
      window.addEventListener('online', online)
      window.addEventListener('offline', offline)
      return () => {
        window.removeEventListener('online', online)
        window.removeEventListener('offline', offline)
      }
    },
  },

  print: {
    async prescription(html, size) {
      const frame = document.createElement('iframe')
      frame.style.position = 'fixed'
      frame.style.inset = '0'
      frame.style.width = '0'
      frame.style.height = '0'
      frame.style.border = '0'
      document.body.appendChild(frame)
      const doc = frame.contentDocument
      if (!doc) return
      doc.open()
      doc.write(`<!doctype html><html><head><style>@page{size:${size};margin:12mm}</style></head><body>${html}</body></html>`)
      doc.close()
      frame.contentWindow?.focus()
      frame.contentWindow?.print()
      window.setTimeout(() => frame.remove(), 1000)
    },
  },

  // A browser has neither. The UI hides both affordances rather than showing
  // a control that cannot work.
  biometrics: null,
  scanner: null,

  privacyScreen: { enable: () => {}, disable: () => {} },
}
